// API client. Session lives in an HttpOnly cookie; all writes are JSON (required by the API's CSRF guard).
export class ApiError extends Error {
  constructor(public status: number, public code: string, public details?: unknown) { super(code); }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch('/api' + url, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined ? { 'content-type': 'application/json' } : {},
      body: body !== undefined ? JSON.stringify(body) : undefined
    });
  } catch {
    throw new ApiError(0, 'network_error');
  }
  const isJson = res.headers.get('content-type')?.includes('json');
  const data = isJson ? await res.json().catch(() => null) : await res.text();
  if (!res.ok) {
    const code = (data && typeof data === 'object' && 'error' in data ? String((data as { error: unknown }).error) : '')
      || (res.status >= 502 && res.status <= 504 ? 'service_unavailable' : 'generic');   // the hosting edge answered, not VegaPart
    if (res.status === 401 && !url.startsWith('/auth/')) window.dispatchEvent(new CustomEvent('fg:unauthorized', { detail: code }));
    // The company just started requiring 2FA (or the session needs it): reload the session so the 2FA screen shows
    if (res.status === 403 && (code === 'mfa_setup_required' || code === 'mfa_required')) window.dispatchEvent(new CustomEvent('fg:mfa'));
    throw new ApiError(res.status, code, (data as { details?: unknown })?.details);
  }
  return data as T;
}

export const api = {
  get: <T = any>(u: string) => request<T>('GET', u),
  post: <T = any>(u: string, b: unknown = {}) => request<T>('POST', u, b),
  put: <T = any>(u: string, b: unknown) => request<T>('PUT', u, b),
  del: <T = any>(u: string) => request<T>('DELETE', u)
};

export const qs = (o: Record<string, unknown>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== null && v !== '' && v !== false) p.set(k, String(v));
  const s = p.toString();
  return s ? '?' + s : '';
};
